import Link from 'next/link';
import type { MouseEvent } from 'react';

// Re-export components that are still needed from the original pagination
export { PaginationEllipsisItem, usePaginationRange } from './pagination';

export function LinkPaginationNavigationButton({
  direction,
  enabled,
  href,
  onClick,
  onMouseEnter,
  onTouchStart,
}: {
  direction: 'previous' | 'next';
  enabled: boolean;
  href?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onTouchStart?: () => void;
}) {
  const isPrevious = direction === 'previous';
  const title = isPrevious ? 'Previous' : 'Next';
  const path = isPrevious
    ? 'M15.707 5.293a1 1 0 0 1 0 1.414L10.414 12l5.293 5.293a1 1 0 0 1-1.414 1.414l-6-6a1 1 0 0 1 0-1.414l6-6a1 1 0 0 1 1.414 0Z'
    : 'M8.293 5.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L13.586 12 8.293 6.707a1 1 0 0 1 0-1.414Z';

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const content = (
    <>
      <span className="sr-only">{title}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
      >
        <title>{title}</title>
        <path
          fill="currentColor"
          fillRule="evenodd"
          d={path}
          clipRule="evenodd"
        />
      </svg>
    </>
  );

  if (enabled && href) {
    return (
      <Link
        href={href}
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        className="relative inline-flex h-8 w-8 items-center justify-center border border-[--color]/30 bg-brand-white font-medium text-[--color]/80 text-sm hover:bg-[--color]/5"
      >
        {content}
      </Link>
    );
  }

  if (enabled) {
    return (
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        className="relative inline-flex h-8 w-8 items-center justify-center border border-[--color]/30 bg-brand-white font-medium text-[--color]/80 text-sm hover:bg-[--color]/5"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="relative inline-flex h-8 w-8 cursor-not-allowed items-center justify-center border border-[--color]/30 bg-[--color]/5 font-medium text-[--color]/30 text-sm">
      {content}
    </div>
  );
}

export function LinkPaginationPageNumberButton({
  pageNumber,
  currentPage,
  href,
  onClick,
  onMouseEnter,
  onTouchStart,
}: {
  pageNumber: number;
  currentPage: number;
  href?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onTouchStart?: () => void;
}) {
  const isCurrentPage = pageNumber === currentPage;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const className = `relative inline-flex h-8 w-8 items-center justify-center border font-medium text-sm ${
    isCurrentPage
      ? 'border-[--color] bg-[--color] text-white cursor-default'
      : 'border-[--color]/30 bg-white text-[--color]/80 hover:bg-[--color]/5'
  }`;

  if (href && !isCurrentPage) {
    return (
      <Link
        href={href}
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        className={className}
        aria-current={isCurrentPage ? 'page' : undefined}
      >
        {pageNumber}
      </Link>
    );
  }

  if (!isCurrentPage) {
    return (
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        className={className}
      >
        {pageNumber}
      </button>
    );
  }

  // Current page - render as span for accessibility
  return (
    <span className={className} aria-current="page" aria-disabled="true">
      {pageNumber}
    </span>
  );
}
